// Thin REST client. Auth is the httpOnly session cookie set by POST /v1/session;
// mutating requests carry the CSRF token, platform admins may pick a tenant.

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;
let tenantRef: string | null = null;

export function setCsrf(token: string | null) {
  csrfToken = token;
}
export function setTenant(ref: string | null) {
  tenantRef = ref;
}
export function currentTenant() {
  return tenantRef;
}

type Query = Record<string, string | number | boolean | string[] | undefined | null>;

export function buildQuery(query?: Query): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => p.append(k, item));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function api<T = any>(
  path: string,
  opts: { method?: string; body?: unknown; query?: Query; noTenant?: boolean } = {},
): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-sf-csrf"] = csrfToken;
  if (tenantRef && !opts.noTenant) headers["x-sf-tenant"] = tenantRef;
  const res = await fetch(path + buildQuery(opts.query), {
    method,
    headers,
    credentials: "same-origin",
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new ApiError(res.status, err.code ?? "http_error", err.message ?? res.statusText, err.details);
  }
  return data as T;
}

// ---- types --------------------------------------------------------------
export interface Principal {
  subject: string;
  name: string;
  tenant_id: string | null;
  permissions: string[];
  group_ids: string[] | null;
  log_group_ids: string[] | null;
  is_platform_admin: boolean;
}
export interface Tenant { id: string; slug: string; name: string; status: string }
export interface TenantLimits {
  events_per_sec: number; bytes_per_day: number; max_groups: number; max_log_groups: number;
  max_subscriptions: number; max_keys: number; max_payload_bytes: number;
}
export const LIMIT_FIELDS: { key: keyof TenantLimits; label: string }[] = [
  { key: "events_per_sec", label: "Events / sec" },
  { key: "bytes_per_day", label: "Bytes / day" },
  { key: "max_groups", label: "Max groups" },
  { key: "max_log_groups", label: "Max log groups" },
  { key: "max_subscriptions", label: "Max subscriptions" },
  { key: "max_keys", label: "Max keys" },
  { key: "max_payload_bytes", label: "Max payload bytes" },
];
export interface Group { id: string; name: string; description: string }
export interface LogGroup {
  id: string; name: string; group_id: string; group_name: string;
  retention_days: number; description: string;
}
export interface LogStream { id: string; name: string; first_event_at: string | null; last_event_at: string | null }
export interface LogEvent {
  event_id: string; timestamp: number; ingestion_time: number; message: string;
  level: string | null; fields: Record<string, unknown> | null; log_stream?: string;
}
export type IssueStatus = "open" | "acknowledged" | "retrying" | "dead_lettered" | "resolved" | "completed";
export interface Issue {
  id: string; fingerprint: string; kind: "exception" | "processing"; status: IssueStatus;
  regressed: boolean; title: string; error_type: string; message: string; level: string;
  component: string | null; operation: string | null; item_key: string | null;
  count: number; attempts: number; max_attempts: number | null;
  first_seen: number; last_seen: number; status_changed_at: number; status_changed_by: string;
  group_id: string; log_group_id: string; last_payload_id: string | null;
  group?: string; log_group?: string;
}
export interface Frame { filename?: string; function?: string; module?: string; lineno?: number; in_app?: boolean; context_line?: string }
export interface Occurrence {
  event_id: string; timestamp: number; kind: string; level: string; error_type: string; message: string;
  stacktrace: Frame[] | null; context: Record<string, unknown> | null; tags: Record<string, string> | null;
  component: string | null; operation: string | null; item_key: string | null; attempt: number | null;
  release: string | null; environment: string | null; log_stream: string | null; payload_id: string | null;
}
export interface HistoryEntry { id: number; at: number; actor: string; action: string; from: string | null; to: string | null; note: string | null }
export interface Subscription {
  id: string; name: string; group_id: string | null; log_group_id: string | null; pattern: string;
  event_types: string[]; status: "active" | "paused";
  destination: { type: "durable" | "webhook"; url: string | null; batch_size: number; max_attempts: number };
  backlog?: { pending: number; ack_pending: number; redelivered: number };
}
export interface ApiKey {
  id: string; name: string; prefix: string; permissions: string[]; group_ids: string[] | null;
  log_group_ids: string[] | null; created_at: string; last_used_at: string | null; revoked_at: string | null;
}
export interface DeadLetter { id: string; attempts: number; last_status: number | null; last_error: string | null; created_at: number; payload: unknown }

// ---- issue lifecycle (mirrors services/lifecycle.py) ----------------------
const ACTIVE: IssueStatus[] = ["open", "acknowledged", "retrying", "dead_lettered"];
export const ACTIONS: Record<string, { from: IssueStatus[]; processingOnly: boolean; label: string }> = {
  acknowledge: { from: ["open", "retrying", "dead_lettered"], processingOnly: false, label: "Acknowledge" },
  resolve: { from: ACTIVE, processingOnly: false, label: "Resolve" },
  complete: { from: ACTIVE, processingOnly: true, label: "Mark complete" },
  retry: { from: ["open", "acknowledged", "retrying", "dead_lettered"], processingOnly: true, label: "Retry" },
  dead_letter: { from: ["open", "acknowledged", "retrying"], processingOnly: true, label: "Dead-letter" },
  reopen: { from: ["resolved", "completed"], processingOnly: false, label: "Reopen" },
};

export function allowedActions(issue: Pick<Issue, "kind" | "status">): string[] {
  return Object.entries(ACTIONS)
    .filter(([, a]) => a.from.includes(issue.status) && (!a.processingOnly || issue.kind === "processing"))
    .map(([name]) => name);
}

export function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "–";
  const d = new Date(ms);
  return d.toISOString().replace("T", " ").slice(0, 23);
}

export function sinceMs(range: string): number | undefined {
  const units: Record<string, number> = { m: 60_000, h: 3_600_000, d: 86_400_000 };
  const m = /^(\d+)([mhd])$/.exec(range);
  return m ? Date.now() - Number(m[1]) * units[m[2]] : undefined;
}
